import { useCallback } from 'react';
import { inventoryCategoriesService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { InventoryCategory } from '../types';

export function useInventoryCategories(
  options?: ResourceQueryOptions
): ResourceState<InventoryCategory> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      inventoryCategoriesService.getAll(opts) as unknown as Promise<{ data: InventoryCategory[]; meta?: any }>,
    []
  );
  return useResourceCollection<InventoryCategory>(fetchFn, options);
}

export function useInventoryCategory(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<InventoryCategory> {
  const numericId = id ? parseInt(id, 10) : null;
  const fetchFn = useCallback(
    (categoryId: number, opts?: ResourceQueryOptions) =>
      inventoryCategoriesService.getById(categoryId, opts) as unknown as Promise<InventoryCategory>,
    []
  );
  return useResourceById<InventoryCategory>(fetchFn, numericId, options);
}

export function useCreateInventoryCategory(
  onSuccess?: (data: InventoryCategory) => void
): MutationState<InventoryCategory> {
  const createFn = useCallback(
    (data: any) => inventoryCategoriesService.create(data) as unknown as Promise<InventoryCategory>,
    []
  );
  return useCreateResource<InventoryCategory>(createFn, onSuccess);
}

export function useUpdateInventoryCategory(
  onSuccess?: (data: InventoryCategory) => void
): MutationState<InventoryCategory> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
      return inventoryCategoriesService.update(numericId, data) as unknown as Promise<InventoryCategory>;
    },
    []
  );
  return useUpdateResource<InventoryCategory>(updateFn, onSuccess);
}

/**
 * Delete inventory category using documentId
 * @param data - Full category object containing documentId
 */
export function useDeleteInventoryCategory(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return inventoryCategoriesService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}

